import { DesignsToken } from '@repo/tokens-studio';
import { captureException } from '@sentry/react';

import { AlphaLyrae } from '@web/ui/font/alpha-lyrae';
import { Chillax } from '@web/ui/font/chillax';
import { MartianMono } from '@web/ui/font/martian-mono';
import { BodyRelative, MonoRelative } from '@web/ui/font/relative';
import { SundryCondensed } from '@web/ui/font/sundry-condensed';
import { env } from '@web/utils/env';

const DesignsTokenFontsMapping = {
  'Alpha Lyrae': AlphaLyrae,
  'Martian Mono': MartianMono,
  'Sundry Condensed': SundryCondensed,
  'Chillax Variable': Chillax,
  'Relative Pro': BodyRelative,
  'Relative Faux Pro': MonoRelative,
};

const mapping = Object.entries(DesignsTokenFontsMapping).reduce<
  Record<string, string>
>(
  (acc, [alias, font]) => ({
    [alias]: font,
    [`'${alias}'`]: font,
    ...acc,
  }),
  {},
);
function getLocalFont(designsTokenFont: string) {
  if (!mapping[designsTokenFont]) {
    if (env.ENVIRONMENT === 'local') {
      throw new Error(`Missing font: ${designsTokenFont}`);
    }

    captureException(new Error('Missing font.'), {
      level: 'fatal',
      extra: {
        designsTokenFont,
      },
    });

    return null;
  }

  return mapping[designsTokenFont]!;
}

/**
 * Local font family names are only known after the Next.js build process. Therefore,
 * we cannot determine in advance how to map font families from design tokens to the local fonts.
 * See https://github.com/vercel/next.js/pull/53608
 */
export function buildTypography(designsToken: DesignsToken) {
  const typefaces = Object.values(designsToken.typeface);
  const replaceIn = {
    typography: designsToken.typography,
    typeface: designsToken.typeface,
  };

  let stringified = JSON.stringify(replaceIn);

  for (const typeface of typefaces) {
    const localFont = getLocalFont(typeface);
    if (localFont) {
      stringified = stringified.replaceAll(typeface, localFont);
    }
  }

  try {
    return JSON.parse(stringified) as typeof replaceIn;
  } catch (e) {
    captureException(e);
    return replaceIn;
  }
}
